// frontend/src/pages/master/VariantOptionMaster.jsx

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { AppSettings } from '../../config/app-settings.js';

const VariantOptionMaster = () => {
  const context = useContext(AppSettings);
  const user_data = context.userInfo;
  const user = user_data ? user_data.user_info : null;
  const authorities = user_data ? user_data.authorities : null;

  const [tabs, setTabs] = useState([]);
  const [options, setOptions] = useState([]);
  const [activeTabUuid, setActiveTabUuid] = useState('');
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false);

  const [isAddingType, setIsAddingType] = useState(false);
  const [newType, setNewType] = useState({ option_type: '', label: '' });
  const inputRef = useRef(null);

  const [isEditingTypeMode, setIsEditingTypeMode] = useState(false);
  const [editTypeData, setEditTypeData] = useState({ option_type: '', label: '' });

  const [editingOptionUuid, setEditingOptionUuid] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);

  const [formData, setFormData] = useState({
    name: '', color_code: '#000000', english_name: '', short_name: ''
  });

  // D&D用のステート（リアルタイム・ゴースト用）
  const [draggableRowId, setDraggableRowId] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);

  const ICON_MAP = {
    color: { icon: 'fa-palette', colorClass: 'text-danger' },
    size: { icon: 'fa-ruler', colorClass: 'text-primary' },
    width: { icon: 'fa-shoe-prints', colorClass: 'text-warning' },
    length: { icon: 'fa-shoe-prints', colorClass: 'text-info' },
    type: { icon: 'fa-shapes', colorClass: 'text-success' },
    material: { icon: 'fa-layer-group', colorClass: 'text-secondary' },
    shape: { icon: 'fa-cube', colorClass: 'text-dark' },
    metal: { icon: 'fa-ring', colorClass: 'text-warning' },
    yukata_color: { icon: 'fa-palette', colorClass: 'text-danger' },
    obi_color: { icon: 'fa-palette', colorClass: 'text-warning' },
    yukata_pattern: { icon: 'fa-shapes', colorClass: 'text-success' }
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (isAddingType && inputRef.current) inputRef.current.focus();
  }, [isAddingType]);

  useEffect(() => {
    const userRole = user?.role || user?.auth_class || authorities?.[0]?.role || 'guest';
    const allowedRoles = ['admin', 'director', 'manager'];
    setHasPermission(allowedRoles.includes(userRole));
  }, [user_data]);

  const loadMasterData = async () => {
    setLoading(true);
    try {
      const [resTypes, resOptions] = await Promise.all([
        apiFetch('/variant-options/types'),
        apiFetch('/variant-options')
      ]);

      if (resTypes.ok && resOptions.ok) {
        const typesData = await resTypes.json();
        const optionsData = await resOptions.json();
        setTabs(typesData);
        setOptions(optionsData);
        if (typesData.length > 0 && !activeTabUuid) {
          setActiveTabUuid(typesData[0].uuid);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleTabChange = (uuid) => {
    setActiveTabUuid(uuid);
    setIsAddingType(false);
    setIsEditingTypeMode(false);
    resetOptionForm();
  };

  const handleAddNewTypeSubmit = async () => {
    if (!hasPermission) return;
    const key = newType.option_type.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const labelText = newType.label.trim() || key;
    if (!key) { setIsAddingType(false); return; }

    try {
      const res = await apiFetch('/variant-options/types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_type: key, label: labelText })
      });
      if (res.ok) {
        const createdType = await res.json();
        alert(`新しい属性「${labelText}」を作成しました！`);
        setActiveTabUuid(createdType.uuid);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の追加に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }

    setIsAddingType(false);
    setNewType({ option_type: '', label: '' });
  };

  const handleAddNewTypeKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsAddingType(false);
      setNewType({ option_type: '', label: '' });
    }
  };

  const handleEditTypeStart = (currentType) => {
    if (!hasPermission) return;
    setEditTypeData({ option_type: currentType.option_type, label: currentType.label });
    setIsEditingTypeMode(true);
  };

  const handleEditTypeSubmit = async () => {
    if (!hasPermission) return;
    const key = editTypeData.option_type.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const labelText = editTypeData.label.trim();
    if (!key || !labelText) { alert('IDと表示名は入力必須です。'); return; }

    try {
      const res = await apiFetch(`/variant-options/types/${activeTabUuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_type: key, label: labelText })
      });
      if (res.ok) {
        setIsEditingTypeMode(false);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の変更に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
  };

  const handleDeleteType = async (currentType) => {
    if (!hasPermission) return;
    const msg = `【超重要アラート】\n属性「${currentType.label}」を完全に削除します。\n子データも一括削除されますが、本当によろしいですか？`;
    if (!window.confirm(msg)) return;

    try {
      const res = await apiFetch(`/variant-options/types/${activeTabUuid}`, { method: 'DELETE' });
      if (res.ok) {
        setActiveTabUuid('');
        setIsEditingTypeMode(false);
        loadMasterData();
      } else {
        alert((await res.json()).detail || '属性の削除に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
  };

  const resetOptionForm = () => {
    setEditingOptionUuid(null);
    setFormData({ name: '', color_code: '#000000', english_name: '', short_name: '' });
  };

  // 🌟 改善: 選択肢の編集開始処理（同じ行をもう一度クリックしたら解除するトグル機能を搭載）
  const handleOptionEditStart = (option) => {
    if (!hasPermission) return;

    // 💡 すでに編集中（選択中）の行をもう一度クリックした場合は、編集モードを解除して新規追加に戻る
    if (editingOptionUuid === option.uuid) {
      resetOptionForm();
      return;
    }

    setEditingOptionUuid(option.uuid);
    setFormData({
      name: option.name || '',
      color_code: option.color_code || '#000000',
      english_name: option.english_name || '',
      short_name: option.short_name || ''
    });
  };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission) return alert("エラー: 権限がありません。");

    try {
      const url = editingOptionUuid ? `/variant-options/${editingOptionUuid}` : '/variant-options';
      const method = editingOptionUuid ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, variant_type_uuid: activeTabUuid })
      });
      if (!res.ok) throw new Error((await res.json()).detail || '保存に失敗しました');

      resetOptionForm();
      loadMasterData();
    } catch (err) { alert(err.message); }
  };

  const handleOptionDelete = async (uuid) => {
    if (!hasPermission) return;
    if (!window.confirm('この選択肢を削除しますか？')) return;
    try {
      const res = await apiFetch(`/variant-options/${uuid}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('削除に失敗しました');
      loadMasterData();
    } catch (err) { alert(err.message); }
  };

  // D&D イベントハンドラー（リアルタイム・ゴーストプレビュー版）
  const handleDragStart = (e, index) => {
    if (!hasPermission) { e.preventDefault(); return; }
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget && e.currentTarget.parentNode) {
      e.dataTransfer.setDragImage(e.currentTarget.parentNode, 20, 20);
    }
  };

  const handleDragEnter = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIndex) return;

    const updatedFiltered = [...filteredOptions];
    const draggedOption = updatedFiltered[draggedIdx];

    updatedFiltered.splice(draggedIdx, 1);
    updatedFiltered.splice(targetIndex, 0, draggedOption);

    const reorderedFiltered = updatedFiltered.map((opt, i) => ({
      ...opt,
      sort_order: i + 1
    }));

    const otherOptions = options.filter(o => o.variant_type_uuid !== activeTabUuid);
    setOptions([...otherOptions, ...reorderedFiltered]);
    setDraggedIdx(targetIndex);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedIdx(null);
    setDraggableRowId(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDraggableRowId(null);
  };

  const handleSaveSortOrder = async () => {
    if (!hasPermission) return;
    setSorting(true);
    const uuidsPayload = filteredOptions.map(o => o.uuid);
    try {
      const res = await apiFetch('/variant-options/sort', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids: uuidsPayload })
      });
      if (res.ok) {
        alert('✨ バリエーションの並び順をマスターに完全保存しました！');
        loadMasterData();
      } else {
        alert('並び順の保存に失敗しました。');
      }
    } catch (e) { alert('通信エラーが発生しました。'); }
    setSorting(false);
  };

  const activeTypeInfo = tabs.find(t => t.uuid === activeTabUuid) || { option_type: 'color', label: '属性' };
  const filteredOptions = options.filter(o => o.variant_type_uuid === activeTabUuid).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const isColorTab = activeTypeInfo.option_type === 'color' || activeTypeInfo.option_type.includes('color');

  return (
    <div className="pb-5">
      <style>{`
        .row-clickable { transition: background-color 0.15s ease; cursor: pointer; }
        .row-clickable:hover { background-color: #f1f5f9; }
        .row-ghost { 
          opacity: 0.2; 
          background-color: #f8fafc;
          outline: 2px dashed #94a3b8;
          outline-offset: -2px;
        }
      `}</style>
      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item active">属性選択肢マスター</li>
          </ol>
          <h2 className="page-header mb-0 fw-bold">バリエーション属性 選択肢マスター管理</h2>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 bg-light rounded shadow-sm border">
        {tabs.map(type => {
          const meta = ICON_MAP[type.option_type] || { icon: 'fa-tag', colorClass: 'text-indigo' };
          return (
            <button
              key={type.uuid}
              className={`btn fw-bold px-4 rounded-pill transition-all ${activeTabUuid === type.uuid ? 'btn-dark shadow' : 'btn-white border bg-white text-secondary'}`}
              onClick={() => handleTabChange(type.uuid)}
            >
              <i className={`fa ${meta.icon} me-2 ${activeTabUuid === type.uuid ? 'text-white' : meta.colorClass}`}></i>
              {type.label}
            </button>
          );
        })}

        {isAddingType ? (
          <div className="d-flex align-items-center gap-1 p-1 bg-white border rounded-pill shadow-sm" style={{ paddingLeft: '12px' }}>
            <i className="fa fa-plus text-muted ms-1"></i>
            <input
              type="text" className="form-control form-control-sm border-0 shadow-none fw-bold bg-transparent" placeholder="ID (例: flavor)"
              style={{ width: '120px' }}
              value={newType.option_type} onChange={e => setNewType({ ...newType, option_type: e.target.value })}
              onKeyDown={handleAddNewTypeKeyDown}
              disabled={!hasPermission}
            />
            <div className="vr opacity-25"></div>
            <input
              type="text" className="form-control form-control-sm border-0 shadow-none fw-bold bg-transparent" placeholder="表示名 (例: 味)"
              style={{ width: '120px' }}
              value={newType.label} onChange={e => setNewType({ ...newType, label: e.target.value })}
              onKeyDown={handleAddNewTypeKeyDown}
              disabled={!hasPermission}
            />
            <button type="button" className="btn btn-sm btn-success rounded-circle shadow-sm ms-1" onClick={handleAddNewTypeSubmit}><i className="fa fa-check"></i></button>
            <button type="button" className="btn btn-sm btn-light rounded-circle text-muted mx-1" onClick={() => { setIsAddingType(false); setNewType({ option_type: '', label: '' }); }}><i className="fa fa-times"></i></button>
          </div>
        ) : (
          hasPermission && (
            <button className="btn btn-outline-secondary fw-bold rounded-pill border-dashed px-3" onClick={() => setIsAddingType(true)}>
              <i className="fa fa-plus me-1"></i>新規属性
            </button>
          )
        )}
      </div>

      <div className="row gx-4">
        {/* 左側：選択肢追加・編集フォーム */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
            <div className={`card-header text-white fw-bold py-3 ${editingOptionUuid ? 'bg-indigo' : 'bg-dark'}`}>
              {editingOptionUuid ? <><i className="fa fa-edit me-2"></i>選択肢を編集・上書き</> : <><i className="fa fa-plus-circle me-2"></i>{activeTypeInfo.label}に新しい選択肢を追加</>}
            </div>
            <div className="card-body p-4">

              {!hasPermission && (
                <div className="alert alert-warning border-0 shadow-sm mb-4 small fw-bold text-dark d-flex align-items-center">
                  <i className="fa fa-exclamation-triangle fs-4 me-3 text-warning"></i>
                  <div>閲覧専用モード：マスターの追加・変更・並び替え権限がありません</div>
                </div>
              )}

              <form onSubmit={handleOptionSubmit}>
                <div className="mb-3">
                  <label className="form-label small text-muted fw-bold">表示名 *</label>
                  <input type="text" className="form-control fw-bold form-control-lg" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="例: ホワイト、LLサイズ" required disabled={!hasPermission} />
                </div>

                {isColorTab && (
                  <div className="mb-3 p-3 bg-light rounded border border-danger border-opacity-25">
                    <label className="form-label small fw-bold text-danger"><i className="fa fa-palette me-1"></i>色選択</label>
                    <div className="d-flex gap-2">
                      <input type="color" className="form-control form-control-color shadow-sm border-0" style={{ width: '60px', height: '38px', padding: '0' }} value={formData.color_code || '#000000'} onChange={e => setFormData({ ...formData, color_code: e.target.value })} disabled={!hasPermission} />
                      <input type="text" className="form-control font-monospace" value={formData.color_code || ''} onChange={e => setFormData({ ...formData, color_code: e.target.value })} placeholder="#000000" disabled={!hasPermission} />
                    </div>
                  </div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted fw-bold">英語名</label>
                    <input type="text" className="form-control" value={formData.english_name} onChange={e => setFormData({ ...formData, english_name: e.target.value })} placeholder="例: White" disabled={!hasPermission} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted fw-bold">省略名</label>
                    <input type="text" className="form-control font-monospace" value={formData.short_name} onChange={e => setFormData({ ...formData, short_name: e.target.value })} placeholder="例: WHT" disabled={!hasPermission} />
                  </div>
                </div>

                {hasPermission && (
                  <div className="d-flex gap-2 mt-3">
                    {editingOptionUuid && (
                      <button type="button" className="btn btn-outline-success fw-bold px-3 text-nowrap" onClick={resetOptionForm}>
                        ➕ 新規追加に戻る
                      </button>
                    )}
                    <button type="submit" className={`btn w-100 fw-bold shadow-sm py-2 ${editingOptionUuid ? 'btn-indigo' : 'btn-success'}`}>
                      <i className="fa fa-save me-2"></i>{editingOptionUuid ? '変更を保存する' : 'この内容でマスタ登録'}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* 右側：登録済み一覧テーブル */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom pt-4 pb-3 d-flex align-items-center justify-content-between">
              {isEditingTypeMode ? (
                <div className="d-flex align-items-center gap-2 w-100 bg-warning bg-opacity-10 p-2 rounded border border-warning border-opacity-25">
                  <span className="small fw-bold text-warning font-monospace">属性編集:</span>
                  <input type="text" className="form-control form-control-sm fw-bold font-monospace" style={{ width: '130px' }} value={editTypeData.option_type} onChange={e => setEditTypeData({ ...editTypeData, option_type: e.target.value })} disabled={!hasPermission} />
                  <input type="text" className="form-control form-control-sm fw-bold" style={{ width: '130px' }} value={editTypeData.label} onChange={e => setEditTypeData({ ...editTypeData, label: e.target.value })} disabled={!hasPermission} />
                  <button type="button" className="btn btn-sm btn-success fw-bold px-3 shadow-sm" onClick={handleEditTypeSubmit}><i className="fa fa-save me-1"></i>保存</button>
                  <button type="button" className="btn btn-sm btn-light border px-2 text-muted" onClick={() => setIsEditingTypeMode(false)}><i className="fa fa-times"></i></button>
                </div>
              ) : (
                <>
                  <h5 className="mb-0 fw-bold">
                    <i className={`fa ${ICON_MAP[activeTypeInfo.option_type]?.icon || 'fa-list'} text-primary me-2`}></i>
                    登録済みの {activeTypeInfo.label} 一覧
                  </h5>

                  <div className="d-flex gap-2">
                    {hasPermission && filteredOptions.length > 1 && (
                      <button type="button" className="btn btn-sm btn-indigo fw-bold px-3 rounded shadow-sm text-white" onClick={handleSaveSortOrder} disabled={sorting}>
                        {sorting ? <i className="fa fa-spinner fa-spin me-1"></i> : <i className="fa fa-save me-1"></i>}
                        並び順をマスターに確定保存
                      </button>
                    )}

                    {hasPermission && activeTypeInfo.option_type !== 'color' && activeTypeInfo.option_type !== 'size' && (
                      <>
                        <button type="button" className="btn btn-xs btn-outline-warning fw-bold px-2 rounded" onClick={() => handleEditTypeStart(activeTypeInfo)}>
                          <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn btn-xs btn-outline-danger fw-bold px-2 rounded" onClick={() => handleDeleteType(activeTypeInfo)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table align-middle mb-0" style={{ borderCollapse: 'collapse', borderSpacing: 0 }}>
                  <thead className="table-light small text-muted">
                    <tr>
                      <th className="text-center" style={{ width: '50px' }}><i className="fa fa-sort"></i></th>
                      {isColorTab && <th style={{ width: '70px' }}>色</th>}
                      <th>表示名</th>
                      <th>英語名 / 省略名</th>
                      {hasPermission && <th className="text-center" style={{ width: '70px' }}>操作</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="5" className="text-center py-5"><i className="fa fa-spinner fa-spin me-2"></i>読み込み中...</td></tr> : filteredOptions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          <i className="fa fa-folder-open fa-2x mb-3 opacity-25 d-block"></i>
                          登録データがありません。
                        </td>
                      </tr>
                    ) : (
                      filteredOptions.map((o, idx) => {
                        const isDraggingRow = draggedIdx === idx;

                        return (
                          <tr
                            key={o.uuid}
                            className={isDraggingRow ? 'row-dragging' : `row-clickable ${editingOptionUuid === o.uuid ? 'bg-indigo bg-opacity-10' : 'bg-white'}`}
                            onClick={() => { if (hasPermission) handleOptionEditStart(o); }}

                            onDragEnter={(e) => handleDragEnter(e, idx)}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}

                            style={{ borderBottom: '1px solid #e2e8f0' }}
                          >
                            <td
                              className="text-center text-muted py-3"
                              draggable={hasPermission && draggableRowId === o.uuid}
                              onDragStart={(e) => handleDragStart(e, idx)}

                              onMouseDown={() => hasPermission && setDraggableRowId(o.uuid)}
                              onMouseUp={() => setDraggableRowId(null)}
                              onMouseLeave={() => setDraggableRowId(null)}
                              onClick={(e) => e.stopPropagation()}

                              style={{ cursor: hasPermission ? 'grab' : 'not-allowed' }}
                            >
                              <i className="fa fa-grip-vertical opacity-50 fs-5"></i>
                            </td>

                            {isColorTab && (
                              <td>
                                <div className="border rounded shadow-sm" style={{ width: '28px', height: '28px', backgroundColor: o.color_code || 'transparent' }}></div>
                              </td>
                            )}
                            <td className={`fw-bold fs-6 ${editingOptionUuid === o.uuid ? 'text-indigo' : ''}`}>
                              {o.name} {editingOptionUuid === o.uuid && '📝'}
                            </td>
                            <td>
                              {o.english_name ? <span className="text-dark me-2">{o.english_name}</span> : <span className="text-muted opacity-5 me-2">--</span>}
                              {o.short_name ? <span className="badge bg-light text-muted border font-monospace px-2">{o.short_name}</span> : ''}
                            </td>

                            {hasPermission && (
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger shadow-sm px-2"
                                  onClick={(e) => { e.stopPropagation(); handleOptionDelete(o.uuid); }}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantOptionMaster;